// frontend/src/hooks/useAuth.tsx
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useLogin, useLogout, useUserProfile } from '../queries/authQueries';
import { loginUser, registerUser, getUserProfile, refreshUserToken } from '../api/auth';
import { usePostHogAnalytics } from './usePostHogAnalytics';
import { useToast } from '../contexts/ToastContext';
import { PostHogEvents } from '../utils/posthogEvents';

// User type from AuthContext for compatibility
export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  birthday?: string | null;
  isVerified: boolean;
  avatar?: string;
  tagline?: string;
  profile_exists?: boolean;
  is_superuser?: boolean;
}

// Compatibility layer to migrate from AuthContext to Zustand
export const useAuth = () => {
  const navigate = useNavigate();
  const { 
    token, 
    user, 
    isLoading, 
    error,
    isAuthenticated: isAuthenticatedState,
    _hasHydrated,
    setToken,
    setUser,
    setLoading,
    setError,
    updateSessionTracking,
    reset
  } = useAuthStore();
  
  const { trackAuth, identifyUser } = usePostHogAnalytics();
  const { refetch: refetchProfile } = useUserProfile();
  const { showToast } = useToast();
  
  // Check if authenticated (function for compatibility)
  const isAuthenticated = useCallback(() => {
    return isAuthenticatedState;
  }, [isAuthenticatedState]);
  
  // Login function (compatible with AuthContext API)
  const login = useCallback(async (
    usernameOrEmail: string, 
    password: string
  ): Promise<{ success: boolean; profileExists: boolean | null }> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await loginUser(usernameOrEmail, password);
      const token = response.access_token || response.token?.access_token;
      
      // Set token in Zustand store
      setToken(token);
      updateSessionTracking();
      
      // Track login
      trackAuth(PostHogEvents.USER_LOGGED_IN, {
        login_method: 'email',
        username_or_email: usernameOrEmail,
        login_timestamp: new Date().toISOString()
      });
      
      // Fetch user profile
      const userResponse = await getUserProfile(token);
      const userData = userResponse.user || userResponse;
      
      const userObj: User = {
        id: userData.user_id || userData.id,
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        birthday: userData.birthday,
        isVerified: userData.email_verified !== undefined ? userData.email_verified : false,
        avatar: userData.avatar || userData.profile?.profile_settings?.avatar_url,
        tagline: userData.profile?.profile_settings?.tagline,
        profile_exists: userData.profile_exists,
        is_superuser: userData.is_superuser,
      };
      
      setUser(userObj);
      
      // Navigate based on profile status
      if (!userObj.isVerified) {
        navigate('/verify-email', { state: { email: userObj.email } });
        return { success: true, profileExists: false };
      } else if (!userObj.profile_exists) {
        navigate('/onboarding');
        return { success: true, profileExists: false };
      }
      
      return { success: true, profileExists: true };
    } catch (error: any) {
      setError(error.message || 'Login failed');
      trackAuth(PostHogEvents.USER_LOGIN_FAILED, {
        login_method: 'email',
        error_message: error.message
      });
      return { success: false, profileExists: null };
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setToken, updateSessionTracking, setUser, navigate, trackAuth]);
  
  // Register function (compatible with AuthContext API)
  const register = useCallback(async (
    email: string,
    username: string,
    password: string
  ): Promise<{ success: boolean; autoLoggedIn?: boolean }> => {
    setLoading(true);
    setError(null);
    
    try {
      await registerUser(email, username, password);
      
      // Auto-login after registration
      try {
        await login(username, password);
        
        trackAuth(PostHogEvents.USER_SIGNED_UP, {
          signup_method: 'email',
          user_journey_stage: 'new',
          auto_logged_in: true,
          signup_timestamp: new Date().toISOString()
        });
        
        navigate('/verify-email', { state: { email } });
        return { success: true, autoLoggedIn: true };
      } catch {
        return { success: true, autoLoggedIn: false };
      }
    } catch (error: any) {
      setError(error.message || 'Registration failed');
      trackAuth(PostHogEvents.USER_SIGNUP_FAILED, {
        signup_method: 'email',
        error_message: error.message
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, login, navigate, trackAuth]);
  
  // Logout function
  const logout = useCallback(async (reason?: string) => {
    // Show a toast if the session expired
    if (reason === 'expired') {
      showToast({
        type: 'warning',
        title: 'Session expired',
        message: 'Please sign in again.'
      });
    }
    trackAuth(PostHogEvents.USER_LOGGED_OUT, {
      user_journey_stage: 'session_end',
      logout_method: reason === 'expired' ? 'auto_expired' : 'user_initiated'
    });
    
    reset();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  }, [reset, navigate, trackAuth]);
  
  // Refresh token function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!token) return false;
    
    try {
      const response = await refreshUserToken(token);
      const newToken = response.access_token || response.token?.access_token;
      setToken(newToken);
      return true;
    } catch {
      return false;
    }
  }, [token, setToken]);
  
  // Fetch user profile
  const fetchUserProfile = useCallback(async (): Promise<User | null> => {
    if (!token) return null;
    
    try {
      const response = await getUserProfile(token);
      const userData = response.user || response;
      
      const userObj: User = {
        id: userData.user_id || userData.id,
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        birthday: userData.birthday,
        isVerified: userData.email_verified !== undefined ? userData.email_verified : false,
        avatar: userData.avatar || userData.profile?.profile_settings?.avatar_url,
        tagline: userData.profile?.profile_settings?.tagline,
        profile_exists: userData.profile_exists,
        is_superuser: userData.is_superuser,
      };
      
      setUser(userObj);
      return userObj;
    } catch {
      return null;
    }
  }, [token, setUser]);
  
  // Get auth headers
  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (!token) return {};
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, [token]);
  
  // Clear auth error
  const clearAuthError = useCallback(() => {
    setError(null);
  }, [setError]);
  
  // Mark profile as completed (for onboarding)
  const markProfileAsCompleted = useCallback((updatedUser: User) => {
    setUser({ ...updatedUser, profile_exists: true });
  }, [setUser]);
  
  // Compatibility functions that don't need implementation with Zustand
  const clearRegistrationFlag = () => {};
  const trackEmailVerification = (userId: string) => {
    trackAuth(PostHogEvents.USER_EMAIL_VERIFIED, {
      user_journey_stage: 'onboarding',
      verification_method: 'email_link'
    });
  };
  
  return {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    hasHydrated: _hasHydrated,
    isRegistering: false, // Not needed with Zustand
    login,
    register,
    logout,
    refreshToken,
    fetchUserProfile,
    getAuthHeaders,
    clearAuthError,
    markProfileAsCompleted,
    clearRegistrationFlag,
    trackEmailVerification
  };
};
